/**
 * Comprehensive node validation and error handling
 * Ensures all node types handle edge cases and invalid inputs gracefully
 */

import { sanitizeHtml, sanitizeVariableName, sanitizeVariableValue } from './security.js';

/**
 * Base node validator with common validation logic
 */
class BaseNodeValidator {
  constructor(nodeType) {
    this.nodeType = nodeType;
    this.errors = [];
    this.warnings = [];
  }

  validate(node) {
    this.errors = [];
    this.warnings = [];

    // Basic structure validation
    if (!node) {
      this.errors.push('Node is null or undefined');
      return this.getResult();
    }

    if (!node.id || typeof node.id !== 'string') {
      this.errors.push('Node missing valid ID');
    }

    if (node.id && node.id.length > 100) {
      this.errors.push('Node ID too long (max 100 characters)');
    }

    if (!node.type || node.type !== this.nodeType) {
      this.errors.push(`Invalid node type: expected ${this.nodeType}, got ${node.type}`);
    }

    if (!node.data || typeof node.data !== 'object') {
      this.errors.push('Node missing data object');
      return this.getResult();
    }

    if (!node.data.config || typeof node.data.config !== 'object') {
      this.errors.push('Node missing config object');
      return this.getResult();
    }

    // Type-specific validation
    this.validateConfig(node.data.config);

    return this.getResult();
  }

  validateConfig(config) {
    // Override in subclasses
  }

  getResult() {
    return {
      valid: this.errors.length === 0,
      errors: [...this.errors],
      warnings: [...this.warnings]
    };
  }

  validateString(value, fieldName, required = false, maxLength = 10000) {
    if (required && (!value || typeof value !== 'string')) {
      this.errors.push(`${fieldName} is required and must be a string`);
      return false;
    }
    if (value && typeof value === 'string' && value.length > maxLength) {
      this.errors.push(`${fieldName} too long (max ${maxLength} characters)`);
      return false;
    }
    return true;
  }

  validateArray(value, fieldName, required = false, maxLength = 100) {
    if (required && !Array.isArray(value)) {
      this.errors.push(`${fieldName} is required and must be an array`);
      return false;
    }
    if (value && Array.isArray(value) && value.length > maxLength) {
      this.errors.push(`${fieldName} too many items (max ${maxLength})`);
      return false;
    }
    return true;
  }

  validateNumber(value, fieldName, required = false, min = null, max = null) {
    if (required && (typeof value !== 'number' || !Number.isFinite(value))) {
      this.errors.push(`${fieldName} is required and must be a finite number`);
      return false;
    }
    if (typeof value === 'number') {
      if (!Number.isFinite(value)) {
        this.errors.push(`${fieldName} must be a finite number`);
        return false;
      }
      if (min !== null && value < min) {
        this.errors.push(`${fieldName} must be at least ${min}`);
        return false;
      }
      if (max !== null && value > max) {
        this.errors.push(`${fieldName} must be at most ${max}`);
        return false;
      }
    }
    return true;
  }
}

/**
 * Message node validator
 */
class MessageValidator extends BaseNodeValidator {
  constructor() {
    super('message');
  }

  validateConfig(config) {
    this.validateString(config.message, 'message', true, 5000);
    
    if (config.messageType && !['text', 'html', 'markdown'].includes(config.messageType)) {
      this.errors.push('messageType must be text, html, or markdown');
    }
  }
}

/**
 * Button node validator
 */
class ButtonValidator extends BaseNodeValidator {
  constructor() {
    super('button');
  }

  validateConfig(config) {
    this.validateArray(config.buttons, 'buttons', true, 20);
    
    if (Array.isArray(config.buttons)) {
      config.buttons.forEach((button, index) => {
        if (!button || typeof button !== 'object') {
          this.errors.push(`Button ${index}: must be an object`);
          return;
        }
        this.validateString(button.text, `Button ${index} text`, true, 200);
        this.validateString(button.value, `Button ${index} value`, false, 200);
      });
    }
  }
}

/**
 * Prompt node validator
 */
class PromptValidator extends BaseNodeValidator {
  constructor() {
    super('prompt');
  }

  validateConfig(config) {
    this.validateString(config.prompt, 'prompt', true, 2000);
    this.validateString(config.variable, 'variable', true, 100);
    
    if (config.variable && !sanitizeVariableName(config.variable)) {
      this.errors.push('Invalid variable name format');
    }
  }
}

/**
 * SetVariable node validator
 */
class SetVariableValidator extends BaseNodeValidator {
  constructor() {
    super('setVariable');
  }

  validateConfig(config) {
    this.validateString(config.variableName, 'variableName', true, 100);
    this.validateString(config.value, 'value', false, 10000);
    
    if (config.scope && !['local', 'global'].includes(config.scope)) {
      this.errors.push('scope must be local or global');
    }
    
    if (config.variableName && !sanitizeVariableName(config.variableName)) {
      this.errors.push('Invalid variable name format');
    }
  }
}

/**
 * Condition node validator
 */
class ConditionValidator extends BaseNodeValidator {
  constructor() {
    super('condition');
  }

  validateConfig(config) {
    this.validateString(config.condition, 'condition', true, 1000);
    
    // Check for potentially dangerous expressions
    if (config.condition) {
      const dangerous = ['eval', 'Function', 'constructor', 'prototype', '__proto__'];
      if (dangerous.some(term => config.condition.includes(term))) {
        this.errors.push('Condition contains potentially dangerous expressions');
      }
    }
  }
}

/**
 * Switch node validator
 */
class SwitchValidator extends BaseNodeValidator {
  constructor() {
    super('switch');
  }

  validateConfig(config) {
    this.validateString(config.variable, 'variable', true, 100);
    this.validateArray(config.cases, 'cases', false, 50);
    
    if (Array.isArray(config.cases)) {
      config.cases.forEach((caseItem, index) => {
        if (!caseItem || typeof caseItem !== 'object') {
          this.errors.push(`Case ${index}: must be an object`);
          return;
        }
        this.validateString(caseItem.value, `Case ${index} value`, true, 200);
        this.validateString(caseItem.label, `Case ${index} label`, false, 200);
      });
    }
  }
}

/**
 * API Call node validator
 */
class ApiCallValidator extends BaseNodeValidator {
  constructor() {
    super('apiCall');
  }

  validateConfig(config) {
    this.validateString(config.url, 'url', true, 2000);
    
    if (config.url) {
      try {
        const url = new URL(config.url);
        if (!['http:', 'https:'].includes(url.protocol)) {
          this.errors.push('URL must use HTTP or HTTPS protocol');
        }
      } catch {
        this.errors.push('Invalid URL format');
      }
    }
    
    if (config.method && !['GET', 'POST', 'PUT', 'DELETE', 'PATCH'].includes(config.method)) {
      this.errors.push('Invalid HTTP method');
    }
    
    this.validateNumber(config.timeout, 'timeout', false, 1000, 60000);
    
    if (config.headers && typeof config.headers !== 'object') {
      this.errors.push('Headers must be an object');
    }
  }
}

/**
 * LLM node validator
 */
class LLMValidator extends BaseNodeValidator {
  constructor() {
    super('llm');
  }

  validateConfig(config) {
    this.validateString(config.model, 'model', false, 100);
    this.validateString(config.systemPrompt, 'systemPrompt', false, 5000);
    this.validateString(config.userPrompt, 'userPrompt', false, 5000);
    this.validateNumber(config.temperature, 'temperature', false, 0, 2);
    this.validateNumber(config.maxTokens, 'maxTokens', false, 1, 4000);
  }
}

/**
 * Code node validator
 */
class CodeValidator extends BaseNodeValidator {
  constructor() {
    super('code');
  }

  validateConfig(config) {
    this.validateString(config.code, 'code', true, 5000);
    this.validateString(config.outputVariable, 'outputVariable', false, 100);
    
    // Check for dangerous code patterns
    if (config.code) {
      const dangerous = [
        'eval', 'Function', 'constructor', 'prototype', '__proto__',
        'import', 'require', 'process', 'global', 'window'
      ];
      if (dangerous.some(term => config.code.includes(term))) {
        this.errors.push('Code contains potentially dangerous expressions');
      }
    }
  }
}

/**
 * Node validator factory
 */
export class NodeValidatorFactory {
  static getValidator(nodeType) {
    const ValidatorClass = nodeValidators[nodeType];
    return ValidatorClass ? new ValidatorClass() : new BaseNodeValidator(nodeType);
  }

  static validateNode(node) {
    if (!node || !node.type) {
      return {
        valid: false,
        errors: ['Invalid node: missing type'],
        warnings: []
      };
    }

    const validator = this.getValidator(node.type);
    return validator.validate(node);
  }

  static validateAllNodes(nodes) {
    if (!Array.isArray(nodes)) {
      return {
        valid: false,
        errors: ['Nodes must be an array'],
        warnings: []
      };
    }

    const results = nodes.map((node, index) => ({
      index,
      nodeId: node?.id || `node-${index}`,
      ...this.validateNode(node)
    }));

    const allErrors = results.flatMap(r => 
      r.errors.map(error => `${r.nodeId}: ${error}`)
    );
    const allWarnings = results.flatMap(r => 
      r.warnings.map(warning => `${r.nodeId}: ${warning}`)
    );

    return {
      valid: allErrors.length === 0,
      errors: allErrors,
      warnings: allWarnings,
      nodeResults: results
    };
  }
}

const nodeValidators = {
  message: MessageValidator,
  button: ButtonValidator,
  prompt: PromptValidator,
  setVariable: SetVariableValidator,
  condition: ConditionValidator,
  switch: SwitchValidator,
  apiCall: ApiCallValidator,
  llm: LLMValidator,
  code: CodeValidator
};

export default NodeValidatorFactory;
